import * as Api from '@ViewModels';
import { ContactsAddTagModal } from '@WebComponents/contacts/ContactsAddTagModal';
import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { toast } from 'sonner';
import { useFullscreenModal, useUserSession } from '../../../models/hooks/appStateHooks';
import { debounce } from '../../../models/UiUtils';
import { useSetKeepInTouchMutation } from '../../../queries/Contact/useSetKeepInTouchMutation';
import { brandSecondary } from '../../styles/colors';
import { AutomationSelectorModal, IAutomationSelectorContactSelection } from '../automation/AutomationSelector';
import { HandwrittenCardTemplateSearchModal } from '../HandwrittenCards/HandwrittenCardTemplateSearchModal';
import { Popover, PopoverContent, PopoverTrigger } from '../Popover';
import { ClockIcon } from '../svgs/icons/ClockIcon';
import { MoreIcon } from '../svgs/icons/MoreIcon';
import { ActionBarButton } from './ActionBarButton';
import { IActionButton } from './ActionBarButtonTypes';
import { ButtonRenderer } from './ButtonRenderer';
import { styleSheet } from './styles';
import { useActionButtons } from './useActionButtons';
import { useButtonConfiguration } from './useButtonConfiguration';

interface IProps {
	criteria?: Api.IFilterCriteria[];
	excludedContacts?: Api.IProjectedContact[];
	getMatchingContacts?: () => Promise<Api.IProjectedContact[]>;
	onDeselectAll?: () => void;
	onExport?: () => void;
	selectedContacts?: Api.IProjectedContact[];
	selectionState: Api.ESelectionState;
	actionButtonsDisabled?: boolean;
	filter?: Api.IContactFilterCriteria[];
	onSendEmailClicked: () => void;
	onTaxLetterClicked?: () => void;
	onDeleteClicked?: () => void;
	deleteDisabled?: boolean;
	showKeepInTouch?: boolean;
}

/**
 * Either `getMatchingContacts` or `selectedContacts && excludedContacts` should be passed in.
 * This is to make integrating back with the people search easier.
 */
export const UsersActionBar: React.FC<IProps> = observer(
	({
		selectionState,
		criteria,
		excludedContacts = [],
		selectedContacts = [],
		getMatchingContacts: propsGetMatchingContacts,
		onExport,
		actionButtonsDisabled = false,
		onSendEmailClicked,
		onTaxLetterClicked,
		onDeleteClicked,
		deleteDisabled = false,
		showKeepInTouch = true,
	}) => {
		const userSession = useUserSession();
		const fullscreenModal = useFullscreenModal();

		// Create ref with callback to measure initial width immediately
		const [containerRefNode, setContainerRefNode] = React.useState<HTMLDivElement | null>(null);
		const containerRef = React.useCallback((node: HTMLDivElement) => {
			if (node !== null) {
				setContainerWidth(node.getBoundingClientRect().width);
				setContainerRefNode(node);
			}
		}, []);

		const setKeepInTouchMutation = useSetKeepInTouchMutation({
			onSuccess: () => {
				toast.success('Keep in touch set successfully');
				setIsKeepInTouchOpen(false);
			},
			onError: () => {
				toast.error('Failed to set keep in touch');
			},
		});

		// Track container width for responsive behavior
		const [containerWidth, setContainerWidth] = React.useState(0);

		// Setup ResizeObserver to measure container width
		React.useEffect(() => {
			if (!containerRefNode) {
				return;
			}

			// Use ResizeObserver to detect container width changes
			const resizeObserver = new ResizeObserver(entries => {
				debounce(() => {
					// Use requestAnimationFrame to avoid ResizeObserver loop errors
					window.requestAnimationFrame(() => {
						if (!entries[0]) return;
						const newWidth = entries[0].contentRect.width;

						// Only update if the width actually changed by at least 1px
						if (Math.abs(newWidth - containerWidth) >= 1) {
							setContainerWidth(newWidth);
						}
					});
				}, 1)();
			});

			resizeObserver.observe(containerRefNode);
			return () => {
				resizeObserver.disconnect();
			};
		}, [containerRefNode, containerWidth]);

		// Ensure we have a getMatchingContacts function
		const getMatchingContacts = React.useCallback(async () => {
			if (propsGetMatchingContacts) {
				return propsGetMatchingContacts();
			}
			// If no getMatchingContacts provided, use the selectedContacts
			return selectedContacts || [];
		}, [propsGetMatchingContacts, selectedContacts]);

		const [contactSelectionForAutomation, setContactSelectionForAutomation] =
			React.useState<IAutomationSelectorContactSelection>(null);
		const [isAutomationOpen, setIsAutomationOpen] = React.useState(false);
		const [isMoreMenuOpen, setIsMoreMenuOpen] = React.useState(false);
		const [isKeepInTouchOpen, setIsKeepInTouchOpen] = React.useState(false);

		const canShowAutomations =
			userSession?.account?.features?.automation?.enabled && userSession?.featurePermissions?.automation?.read;
		const canShowCards = userSession?.account?.features?.handwrittenCards?.enabled;

		const [isHwcTemplateChooserModalOpen, setIsHwcTemplateChooserModalOpen] = React.useState(false);
		const [routeAfterHwcTemplateSelection, setRouteAfterHwcTemplateSelection] = React.useState<'contacts' | 'filter'>(
			null
		);
		const [tagBulkRequest, setTagBulkRequest] = React.useState<Api.IBulkContactsRequest>(null);

		const canSendTextingCampaign =
			userSession?.account?.features?.texting?.enabled &&
			userSession.account.features.automation?.additionalAllowedTriggers?.includes(Api.AutomationTriggerType.Texting);

		const onSendCardClicked = async () => {
			const matchingContacts = await getMatchingContacts();

			if (selectionState === 'all' || selectionState === 'some') {
				setRouteAfterHwcTemplateSelection(matchingContacts.length === 0 ? 'filter' : 'contacts');
				setIsHwcTemplateChooserModalOpen(true);
			}
		};

		const onAddTagClicked = async () => {
			const matchingContacts = await getMatchingContacts();
			setTagBulkRequest({
				excludeContactIds: excludedContacts?.map(c => c.id) ?? [],
				includeContactIds: matchingContacts.map(c => c.id),
				filter: { criteria: [] },
			});
		};

		const onSendTextClicked = async () => {
			const matchingContacts = await getMatchingContacts();

			if (matchingContacts.length > 0) {
				fullscreenModal.history.push({
					pathname: `/texting-campaign/contacts`,
					state: {
						contactIds: matchingContacts.map(contact => contact.id),
					},
				});
			} else {
				fullscreenModal.history.push({
					pathname: `/texting-campaign/filter`,
					state: {
						initialContactIdsToOmit: excludedContacts?.map(contact => contact.id),
						initialFilterCriteriaList: criteria,
					},
				});
			}
		};

		const onHwcTemplateClicked = async (template: Api.IHandwrittenCardTemplate) => {
			const matchingContacts = await getMatchingContacts();
			if (routeAfterHwcTemplateSelection === 'contacts') {
				fullscreenModal.history.push({
					pathname: `/bulk-contact-selection-postcard/${template.id}`,
					state: {
						contactIds: matchingContacts.map(contact => contact.id),
					},
				});
			} else if (routeAfterHwcTemplateSelection === 'filter') {
				fullscreenModal.history.push({
					pathname: `/bulk-contact-filter-postcard/${template.id}`,
					state: {
						contactIdsToOmit: excludedContacts?.map(contact => contact.id),
						tagFilterCriterias: [],
					},
				});
			}
			setIsHwcTemplateChooserModalOpen(false);

			setRouteAfterHwcTemplateSelection(null);
		};

		const onKeepInTouch = async (frequency: number) => {
			if (frequency <= 0) return;

			const matchingContacts = await getMatchingContacts();
			const keepInTouch = {
				accountId: userSession.account.id,
				userId: userSession.user.id,
				kitFrequency: frequency,
			};

			// create the kits using keepInTouch as the base
			const keepInTouches = matchingContacts.map(x => {
				const kit: Api.IKeepInTouch = {
					...keepInTouch,
					contactId: x.id,
				};
				kit.accountId = kit.accountId || userSession.account.id;
				kit.userId = kit.userId || userSession.user.id;
				return kit;
			});

			setKeepInTouchMutation.mutate(keepInTouches);
		};

		// Get all action buttons
		const allButtons = useActionButtons({
			selectionState,
			getMatchingContacts,
			onSendEmailClicked,
			onSendCardClicked,
			onSendTextClicked,
			onAddTagClicked,
			onTaxLetterClicked,
			onDeleteClicked,
			deleteDisabled,
			onExport,
			setContactSelectionForAutomation,
			setIsAutomationOpen,
			canShowAutomations,
			canShowCards,
			canSendTextingCampaign,
			actionButtonsDisabled,
		});

		// Create Keep in Touch button separately
		const keepInTouchButton = {
			key: 'keepInTouch',
			icon: <ClockIcon fillColor={brandSecondary} />,
			label: 'Keep in touch',
			onClick: () => setIsKeepInTouchOpen(true),
			isDisabled: selectionState === 'none' || actionButtonsDisabled,
			styleClass: styleSheet.keepInTouch,
			actionButtonType: 'dropdownTrigger',
		};

		// Add the Keep in Touch button after the merge button (if enabled)
		const allButtonsWithKeepInTouch = showKeepInTouch
			? [...allButtons.slice(0, 5), keepInTouchButton, ...allButtons.slice(5)]
			: allButtons;

		// Determine which buttons to show and which to put in "More" menu
		const { visibleButtons, moreMenuButtons } = useButtonConfiguration(
			allButtonsWithKeepInTouch as IActionButton[],
			containerWidth
		);

		return (
			<div ref={containerRef} className={css(styleSheet.container)}>
				<div className={css(styleSheet.actionButtonsContainer)}>
					{/* Other buttons */}
					<ButtonRenderer
						allButtons={allButtons}
						visibleButtons={visibleButtons.filter(key => key !== 'keepInTouch')}
					/>

					{/* Keep in Touch Button with Popover */}
					{visibleButtons.includes('keepInTouch') && (
						<Popover open={isKeepInTouchOpen} onOpenChange={setIsKeepInTouchOpen}>
							<PopoverTrigger asChild>
								<button
									onClick={() => setIsKeepInTouchOpen(!isKeepInTouchOpen)}
									className={css(
										styleSheet.actionButton,
										keepInTouchButton.isDisabled && styleSheet.menuItemDisabled,
										keepInTouchButton.styleClass
									)}
									disabled={keepInTouchButton.isDisabled}
								>
									{keepInTouchButton.icon}
									<span className={css(styleSheet.buttonText)}>{keepInTouchButton.label}</span>
								</button>
							</PopoverTrigger>
							<PopoverContent>
								<div className={css(styleSheet.keepInTouchMenu)}>
									<button className={css(styleSheet.keepInTouchMenuItem)} onClick={() => onKeepInTouch(1)}>
										<span>Every month</span>
									</button>
									<button className={css(styleSheet.keepInTouchMenuItem)} onClick={() => onKeepInTouch(2)}>
										<span>Every 2 months</span>
									</button>
									<button className={css(styleSheet.keepInTouchMenuItem)} onClick={() => onKeepInTouch(3)}>
										<span>Every 3 months</span>
									</button>
									<button className={css(styleSheet.keepInTouchMenuItem)} onClick={() => onKeepInTouch(6)}>
										<span>Every 6 months</span>
									</button>
									<button className={css(styleSheet.keepInTouchMenuItem)} onClick={() => onKeepInTouch(12)}>
										<span>Every year</span>
									</button>
								</div>
							</PopoverContent>
						</Popover>
					)}

					{moreMenuButtons.length > 0 && (
						<Popover open={isMoreMenuOpen} onOpenChange={setIsMoreMenuOpen}>
							<PopoverTrigger asChild>
								<button
									onClick={() => {
										setIsMoreMenuOpen(!isMoreMenuOpen);
									}}
									className={css(styleSheet.moreButton)}
								>
									<MoreIcon fillColor='#555' />
								</button>
							</PopoverTrigger>
							<PopoverContent>
								<div className={css(styleSheet.moreMenuContainer)}>
									{moreMenuButtons.map(btn => {
										return (
											<div key={btn.key} className={css(styleSheet.moreMenuButton)}>
												<ActionBarButton
													key={btn.key}
													icon={btn.icon}
													onClick={btn.onClick}
													disabled={btn.disabled}
													styleClass={btn.styleClass}
													isFullWidth={true}
												>
													{btn.children}
												</ActionBarButton>
											</div>
										);
									})}
								</div>
							</PopoverContent>
						</Popover>
					)}
				</div>

				{tagBulkRequest ? (
					<ContactsAddTagModal
						request={tagBulkRequest}
						modalProps={{
							isOpen: Boolean(tagBulkRequest),
							onRequestClose: () => setTagBulkRequest(null),
						}}
					/>
				) : null}

				{isAutomationOpen ? (
					<AutomationSelectorModal
						closeModal={() => setIsAutomationOpen(false)}
						startAutomationForSelectedContacts={true}
						contactSelection={contactSelectionForAutomation}
					/>
				) : null}

				{isHwcTemplateChooserModalOpen ? (
					<HandwrittenCardTemplateSearchModal
						isOpen={true}
						onClose={() => setIsHwcTemplateChooserModalOpen(false)}
						onTemplateClicked={onHwcTemplateClicked}
					/>
				) : null}
			</div>
		);
	}
);
